import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  fetchVisitorAvailability,
  fetchVisitorSession,
  SlotTaken,
  submitRequest,
  type VisitorAvailability,
} from "../api.js";
import { AskForAccess } from "../components/AskForAccess.js";
import { Lockup } from "../components/Logo.js";

/**
 * The booking page (2c-2j), in the brand's slot-row form.
 *
 * A visitor sees times and nothing else. There is no indication of why a time
 * is absent - a meeting, a blackout, a buffer, a holiday and an hour already
 * requested by somebody else are all equally just not here.
 *
 * Times are shown in the visitor's own zone, with the host's beneath, because
 * getting that wrong is the classic way to book a 3am meeting.
 */

function dayKey(iso: string, zone: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    timeZone: zone,
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function timeIn(iso: string, zone: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    timeZone: zone,
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Booking() {
  const queryClient = useQueryClient();
  const session = useQuery({ queryKey: ["visitor"], queryFn: fetchVisitorSession });
  const visitorZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const [duration, setDuration] = useState(30);
  const [chosen, setChosen] = useState<string | null>(null);
  const [purpose, setPurpose] = useState("");
  const [flexibility, setFlexibility] = useState("");

  const availability = useQuery<VisitorAvailability>({
    queryKey: ["visitor-availability", duration],
    queryFn: () => fetchVisitorAvailability(duration, 14),
    enabled: session.data?.signedIn === true,
  });

  const send = useMutation({
    mutationFn: () =>
      submitRequest({
        startUtc: chosen!,
        durationMinutes: duration,
        purpose,
        ...(flexibility ? { flexibilityNote: flexibility } : {}),
        visitorTimezone: visitorZone,
      }),
    onSuccess: () => {
      setChosen(null);
      setPurpose("");
      setFlexibility("");
      void queryClient.invalidateQueries({ queryKey: ["visitor-availability"] });
      void queryClient.invalidateQueries({ queryKey: ["visitor-requests"] });
    },
    onError: (error) => {
      // A slot that went is a stale list, not just a failed request: re-read,
      // or the next thing the visitor picks is as likely to be gone as the
      // first. What they typed is deliberately kept.
      if (error instanceof SlotTaken) {
        setChosen(null);
        void queryClient.invalidateQueries({ queryKey: ["visitor-availability"] });
      }
    },
  });

  if (session.isPending) {
    return (
      <main className="centered">
        <p className="lede">Checking your invitation…</p>
      </main>
    );
  }

  // Covers an expired session and a revoked invitation (2t) alike: the session
  // row is deleted in both cases, so there is nothing here that could tell them
  // apart even if we wanted to. The first line is the fix for the common case,
  // the recovery is the fix for the other.
  if (!session.data?.signedIn) {
    return (
      <main className="centered">
        <section className="card">
          <Lockup size={22} />
          <h1>This link needs confirming</h1>
          <p className="lede">
            Open the link from your invitation email again, and we&rsquo;ll send a fresh
            code.
          </p>
          <div className="recovery">
            <h2>Link stopped working?</h2>
            <p className="helper">
              Invitations can be withdrawn, and we don&rsquo;t say when. If yours no
              longer lets you in, you can ask for a new one.
            </p>
            <AskForAccess lead="They decide; we only pass it along." />
          </div>
        </section>
      </main>
    );
  }

  const visitor = session.data.visitor!;
  const data = availability.data;
  const grouped = new Map<string, { startUtc: string; endUtc: string }[]>();
  for (const slot of data?.slots ?? []) {
    const key = dayKey(slot.startUtc, visitorZone);
    grouped.set(key, [...(grouped.get(key) ?? []), slot]);
  }

  return (
    <main className="visitor">
      <header className="visitor-head">
        <Lockup size={20} />
        <span className="eyebrow">Read only</span>
      </header>

      <h1>Meet with {visitor.ownerName}</h1>
      <p className="lede">
        Pick a time to request. {visitor.ownerName} answers by email — nothing is booked
        until they do.
      </p>

      <div className="row">
        {(data?.durations ?? [15, 30, 45, 60]).map((value) => (
          <button
            key={value}
            type="button"
            className={value === duration ? "chip chip-on" : "chip"}
            aria-pressed={value === duration}
            onClick={() => {
              setDuration(value);
              setChosen(null);
            }}
          >
            {value} min
          </button>
        ))}
      </div>

      <p className="helper">
        Times shown in {visitorZone}
        {data && data.ownerTimezone !== visitorZone
          ? ` · ${data.ownerTimezone} for ${visitor.ownerName}`
          : ""}
      </p>

      {send.isSuccess ? (
        <div className="notice" role="status">
          <strong>Requested — not booked yet</strong>
          <span>
            {visitor.ownerName} has your request. Nothing is on either calendar.
            You&rsquo;ll get an email either way, and you can{" "}
            <Link to="/my-requests">see it here</Link>.
          </span>
        </div>
      ) : null}

      {send.isError ? (
        <div className="notice notice-error" role="alert">
          <strong>
            {send.error instanceof SlotTaken
              ? "That time just went"
              : "That didn't send"}
          </strong>
          <span>
            {send.error.message}{" "}
            {send.error instanceof SlotTaken
              ? "The times below have just been read again — pick another and your note is kept."
              : "Try again in a moment; nothing was sent."}
          </span>
        </div>
      ) : null}

      {availability.isPending ? <p className="helper">Finding free times…</p> : null}

      {data?.readAt && Date.now() - new Date(data.readAt).getTime() > 5 * 60_000 ? (
        <div className="notice" role="status">
          <strong>These times were worked out a little while ago</strong>
          <span>
            Free times were read{" "}
            {Math.round((Date.now() - new Date(data.readAt).getTime()) / 60_000)} minutes
            ago — one may have gone since. If it has, we&rsquo;ll say so and keep what you
            wrote.
          </span>
        </div>
      ) : null}

      {data && data.slots.length === 0 ? (
        <div className="notice" role="status">
          <strong>Nothing open in the next two weeks</strong>
          <span>
            Times open up as plans change, but there&rsquo;s no way to be told when —
            checking back here is the only option. Your invitation stays valid.
          </span>
        </div>
      ) : null}

      {[...grouped.entries()].map(([day, slots]) => (
        <section key={day} className="dayblock">
          <h2>{day}</h2>
          <div className="slots">
            {slots.map((slot) => {
              const selected = chosen === slot.startUtc;
              return (
                <button
                  key={slot.startUtc}
                  type="button"
                  className={selected ? "slotrow slotrow-on" : "slotrow"}
                  aria-pressed={selected}
                  aria-label={`${day}, ${timeIn(slot.startUtc, visitorZone)}, ${String(duration)} minutes, ${visitorZone}`}
                  onClick={() => setChosen(selected ? null : slot.startUtc)}
                >
                  <span className="slottime">{timeIn(slot.startUtc, visitorZone)}</span>
                  <span className="slotstate">{selected ? "Selected" : "Free"}</span>
                </button>
              );
            })}
          </div>
        </section>
      ))}

      {chosen ? (
        <form
          className="requestform"
          onSubmit={(event) => {
            event.preventDefault();
            send.mutate();
          }}
        >
          <h2>Request {timeIn(chosen, visitorZone)}</h2>
          <p className="helper">
            {dayKey(chosen, visitorZone)} · {duration} min
            {data
              ? ` · ${timeIn(chosen, data.ownerTimezone)} for ${visitor.ownerName}`
              : ""}
          </p>

          <label className="field">
            <span>What&rsquo;s it about?</span>
            <textarea
              required
              rows={3}
              value={purpose}
              placeholder="Q4 portfolio handover — walk through open items before the board pack."
              onChange={(event) => setPurpose(event.target.value)}
            />
          </label>

          <label className="field">
            <span>Any flexibility? (optional)</span>
            <input
              type="text"
              value={flexibility}
              placeholder="Happy to do 15 min if that's easier."
              onChange={(event) => setFlexibility(event.target.value)}
            />
          </label>

          <div className="row">
            <button type="submit" className="primary" disabled={send.isPending}>
              {send.isPending ? "Sending…" : "Request this time"}
            </button>
            <button type="button" className="ghost" onClick={() => setChosen(null)}>
              Cancel
            </button>
          </div>
          <p className="footnote">
            Invite-only · Read-only · Nothing is booked without {visitor.ownerName}
            &rsquo;s approval.
          </p>
        </form>
      ) : null}
    </main>
  );
}
